/**
 * The pill that says which customer owns a thing.
 *
 * A tinted pill with the customer's NAME inside it — not a coloured dot you
 * have to decode. That distinction is the whole design, and it is what makes
 * the palette work at all.
 *
 * WHY A DOT WOULD HAVE CAPPED AT THREE
 *
 * A coloured dot is an encoding: to use it you must tell customer four's hue
 * from customer five's, and any two can appear side by side in a list. Measured
 * against this app's panel surface (#151a21), on the dark categorical ramp,
 * with red and green removed because they mean BAD and GOOD here:
 *
 *   3 hues (blue, yellow, magenta)  CVD dE 13.2, normal-vision dE 19.3  PASS
 *   4 hues, every combination                                           FAIL
 *   5 hues, every combination                                           FAIL
 *
 * The closest four-hue set puts yellow beside orange at dE 10.6 — below the
 * floor of 15, indistinguishable to a person with ordinary colour vision, never
 * mind a colour-blind one. A fourth generated hue would be a colour code that
 * quietly stops coding.
 *
 * WHY A PILL DOES NOT
 *
 * Put the name inside and the text carries identity, always. Colour drops from
 * encoding to tint — it aids recognition and grouping, and nobody ever has to
 * decode it. Tints also live somewhere else in colour space than chart marks
 * do: a mark needs mid lightness and high chroma to read as a distinct mark,
 * while a pill on a dark surface wants a dim low-chroma wash with bright text
 * on it. That band is roomier, so six hues fit comfortably where three marks
 * did not.
 *
 * WHAT IS STILL FORBIDDEN
 *
 * Red, green and amber. They are the verdict language of this product, read
 * from two metres on a line: green is GOOD, red is BAD, amber is UNSURE. A
 * customer pill in soft red still reads as a rejected part at a glance, and the
 * database enforces the exclusion rather than trusting whoever picks next — see
 * the CHECK constraint on customers.color_hue.
 *
 * Every tone below was measured, not judged: text on pill runs 6.96:1 to
 * 7.97:1, clear of the 4.5:1 floor for text.
 */
/**
 * Usable hues, in assignment order.
 *
 * All between 150 and 340, which is the arc left once green (~120), red (~0)
 * and amber (~42) are removed with margin. Spread across that arc rather than
 * bunched, so neighbours in the list look different even though nothing
 * depends on it.
 */
export const CUSTOMER_HUES = [212, 320, 190, 265, 296, 240];
/** The band the database enforces. Outside it lies the verdict language. */
export const CUSTOMER_HUE_MIN = 150;
export const CUSTOMER_HUE_MAX = 340;
/**
 * The three tones of one pill, derived from a single hue.
 *
 * Derived rather than stored as three hex values, so a pill can never be half
 * one colour and half another — there is one number, and the relationship
 * between the tones is fixed.
 */
export function customerPill(hue) {
    if (hue === null || hue === undefined) {
        // Unassigned: grey, and still perfectly readable. Nothing is lost, because
        // the name is inside the pill either way.
        return { background: '#232a33', text: '#b8c4d0', border: '#39434f' };
    }
    const h = ((hue % 360) + 360) % 360;
    return {
        background: `hsl(${h} 38% 20%)`,
        text: `hsl(${h} 72% 80%)`,
        border: `hsl(${h} 34% 32%)`,
    };
}
/**
 * The hue a newly created customer starts with.
 *
 * Round-robin over how many customers already exist — first come, first served,
 * never by rank or station count. A hue that moved when another customer grew
 * would repaint a pill somebody had already learned. Wrapping past six is fine
 * here in a way it never was for dots: two customers sharing a tint are still
 * told apart instantly by the name printed inside them.
 */
export function defaultCustomerHue(existingCustomers) {
    return CUSTOMER_HUES[existingCustomers % CUSTOMER_HUES.length];
}
/**
 * Initials, for the places too tight for a full name.
 *
 * Two words give two letters ("Bruss-Group North America" -> "BG"), one word
 * gives its first two ("Meat" -> "ME").
 */
export function customerInitials(name) {
    const words = name
        .replace(/[^\p{L}\p{N} ]+/gu, ' ')
        .split(/\s+/)
        .filter(Boolean);
    if (words.length === 0)
        return '?';
    if (words.length === 1)
        return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
}
//# sourceMappingURL=customerColors.js.map